<template>
  <!-- 테스트 따라하기 — 화면 위 ①②③ 배지 + 현재 단계 말풍선 + 하단 진행 바 (2026-10-18) -->
  <teleport to="body">
    <div v-if="tour.active" class="qa-tour-layer qa-tour-ignore">
      <!-- 번호 배지 (찾아진 단계 전부) -->
      <div v-for="b in badges" :key="b.i" class="qa-badge" :class="{ cur: b.i === tour.index, done: b.i < tour.index }"
           :style="{ top: b.top + 'px', left: b.left + 'px' }" @click="go(b.i)">{{ b.i + 1 }}</div>

      <!-- 현재 단계 하이라이트 -->
      <div v-if="curRect" class="qa-spot" :style="{ top: curRect.top + 'px', left: curRect.left + 'px', width: curRect.width + 'px', height: curRect.height + 'px' }"></div>

      <!-- 말풍선 -->
      <div class="qa-tip" :class="{ floating: !curRect }" :style="tipStyle">
        <div class="qa-tip-head">
          <span class="qa-tip-no">{{ tour.index + 1 }}</span>
          <span class="qa-tip-title">{{ tour.scenario.step_no }} {{ tour.scenario.title }}</span>
          <span class="muted">{{ tour.index + 1 }} / {{ tour.steps.length }}</span>
        </div>
        <div class="qa-tip-body">
          {{ cur.text }}
          <span v-if="cur.value" class="qa-val" :title="'클릭하면 복사'" @click="copy(cur.value)"><b>{{ cur.value }}</b>{{ copied ? ' ✓' : ' ⧉' }}</span>
          <div v-if="!curRect && cur.kind !== 'text' && !cur.soft" class="qa-notfound">화면에서 "{{ cur.label || (cur.anyOf || []).join('/') }}" 을 찾지 못했습니다 — 직접 찾아 진행하고 [다음]을 누르세요.
            <el-button v-if="canPick" link type="primary" size="small" @click="startPick">위치 지정</el-button>
          </div>
        </div>
        <div class="qa-tip-actions">
          <el-button size="small" :disabled="tour.index === 0" @click="go(tour.index - 1)">이전</el-button>
          <el-button size="small" type="primary" @click="next">{{ tour.index === tour.steps.length - 1 ? '절차 끝 — 확인할 것 체크' : '다음' }}</el-button>
          <el-button size="small" link @click="qa.openDrawerFromTour()">확인할 것 보기</el-button>
          <el-button size="small" link type="danger" @click="qa.endTour()">중단</el-button>
        </div>
      </div>

      <!-- 하단 진행 바 -->
      <div class="qa-bar">
        <span>🧪 따라하는 중 — <b>{{ tour.scenario.step_no }} {{ tour.scenario.title }}</b></span>
        <el-progress :percentage="Math.round(tour.index * 100 / Math.max(1, tour.steps.length))" :stroke-width="8" style="width: 160px" :show-text="false" />
        <span class="muted">{{ found }}/{{ tour.steps.filter(isAnchorStep).length }}개 위치 찾음</span>
        <el-button size="small" link @click="qa.openDrawerFromTour()">서랍 열기</el-button>
        <el-button size="small" link type="danger" @click="qa.endTour()">중단</el-button>
      </div>

      <!-- 위치 지정 모드 -->
      <div v-if="picking" class="qa-pick-hint">화면에서 이 단계의 버튼/입력칸을 클릭하세요 (Esc 취소)</div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useQaStore } from '@/stores/qa'
import { useUserStore } from '@/stores/user'
import { describeElement, isAnchorStep, resolveStep } from '@/utils/qaTour'

const qa = useQaStore()
const userStore = useUserStore()
const tour = computed(() => qa.tour)
const cur = computed(() => tour.value.steps[tour.value.index] || { text: '' })
const els = ref([])            // 단계별 요소(or null)
const badges = ref([])
const curRect = ref(null)
const copied = ref(false)
const picking = ref(false)
const found = computed(() => els.value.filter((el, i) => el && isAnchorStep(tour.value.steps[i])).length)
const canPick = computed(() => userStore.profile?.is_superuser || (userStore.profile?.groups || []).some(g => ['테스트관리자', '테스트화면담당자'].includes(g)))

function measure() {
  if (!tour.value.active) return
  const out = []
  els.value.forEach((el, i) => {
    if (!el || !el.isConnected) return
    const r = el.getBoundingClientRect()
    if (!r.width && !r.height) return
    out.push({ i, top: Math.max(4, r.top - 10), left: Math.max(4, r.left - 10) })
  })
  badges.value = out
  const el = els.value[tour.value.index]
  if (el && el.isConnected) {
    const r = el.getBoundingClientRect()
    curRect.value = r.width || r.height ? { top: r.top - 4, left: r.left - 4, width: r.width + 8, height: r.height + 8 } : null
  } else curRect.value = null
}

function resolveAll() {
  if (!tour.value.active) return
  els.value = tour.value.steps.map(s => s.kind === 'text' ? null : resolveStep(s))
  measure()
}

const tipStyle = computed(() => {
  const r = curRect.value
  if (!r) return { right: '24px', bottom: '72px' }
  const W = 340, H = 150
  let top, left
  if (r.left + r.width + 12 + W < window.innerWidth - 8) { left = r.left + r.width + 12; top = r.top - 8 }   // 오른쪽 옆 (배지를 안 가림)
  else if (r.left - W - 12 > 8) { left = r.left - W - 12; top = r.top - 8 }                                   // 왼쪽 옆
  else { left = Math.min(r.left, window.innerWidth - W - 8); top = r.top + r.height + 14 }                   // 아래
  if (top + H > window.innerHeight - 60) top = Math.max(8, window.innerHeight - 60 - H)
  if (top < 8) top = 8
  return { top: top + 'px', left: left + 'px', width: W + 'px' }
})

function scrollToCur() {
  const el = els.value[tour.value.index]
  if (el && el.scrollIntoView) { try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }) } catch (e) { /* */ } }
}
function go(i) { qa.tour.index = Math.max(0, Math.min(tour.value.steps.length - 1, i)); measure(); scrollToCur() }
function next() {
  if (tour.value.index >= tour.value.steps.length - 1) { qa.finishTour(); return }
  go(tour.value.index + 1)
}
async function copy(v) { try { await navigator.clipboard.writeText(v) } catch (e) { /* */ } copied.value = true; setTimeout(() => { copied.value = false }, 1200) }

// 사용자가 현재 단계 요소를 실제로 클릭하면 자동으로 다음
function onDocClick(ev) {
  if (!tour.value.active) return
  if (picking.value) { ev.preventDefault(); ev.stopPropagation(); finishPick(ev.target); return }
  const el = els.value[tour.value.index]
  if (el && (el === ev.target || el.contains(ev.target))) {
    const s = cur.value
    if (s.kind === 'input') return // 입력은 값을 넣어야 하니 자동 진행 안 함
    setTimeout(() => { if (tour.value.active && tour.value.index < tour.value.steps.length - 1) go(tour.value.index + 1); else if (tour.value.active) qa.finishTour() }, 350)
  }
}
function onInput(ev) {
  if (!tour.value.active || cur.value.kind !== 'input') return
  const el = els.value[tour.value.index]
  if (el && (el === ev.target || el.contains(ev.target))) {
    clearTimeout(onInput._t); onInput._t = setTimeout(() => { if (tour.value.active) go(tour.value.index + 1) }, 1500)
  }
}
function onKey(ev) { if (ev.key === 'Escape' && picking.value) { picking.value = false; document.body.classList.remove('qa-picking') } }

// 위치 지정 (화면 담당·관리자)
function startPick() { picking.value = true; document.body.classList.add('qa-picking') }
async function finishPick(target) {
  picking.value = false; document.body.classList.remove('qa-picking')
  const desc = describeElement(target)
  if (!desc) return
  const sc = tour.value.scenario
  const anchors = { ...(sc.anchors || {}), [tour.value.index]: desc }
  try {
    await axiosInstance.patch(`qa/scenarios/${sc.id}/`, { anchors })
    sc.anchors = anchors; tour.value.steps[tour.value.index].anchor = desc
    ElMessage.success('위치 저장 — 다음부터 이 단계는 여기를 가리킵니다')
    resolveAll()
  } catch (e) { ElMessage.error('위치 저장 실패') }
}

let mo = null, timer = null
onMounted(() => {
  document.addEventListener('click', onDocClick, true)
  document.addEventListener('input', onInput, true)
  document.addEventListener('keydown', onKey, true)
  window.addEventListener('scroll', measure, true)
  window.addEventListener('resize', measure)
  mo = new MutationObserver(() => { clearTimeout(mo._t); mo._t = setTimeout(resolveAll, 250) })
  mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
  timer = setInterval(measure, 500)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
  document.removeEventListener('input', onInput, true)
  document.removeEventListener('keydown', onKey, true)
  window.removeEventListener('scroll', measure, true)
  window.removeEventListener('resize', measure)
  mo && mo.disconnect(); clearInterval(timer)
})
watch(() => [tour.value.active, tour.value.scenario?.id], () => { if (tour.value.active) { resolveAll(); setTimeout(() => { resolveAll(); scrollToCur() }, 600) } else { els.value = []; badges.value = []; curRect.value = null } }, { immediate: true })
</script>

<style>
/* teleport 라 전역 스타일 */
.qa-tour-layer { position: fixed; inset: 0; pointer-events: none; z-index: 3000; }
.qa-badge { position: fixed; width: 24px; height: 24px; border-radius: 50%; background: #1f3a5f; color: #fff; font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 6px rgba(0,0,0,.35); pointer-events: auto; cursor: pointer; z-index: 3002; border: 2px solid #fff; }
.qa-badge.cur { background: #e6a23c; transform: scale(1.25); animation: qa-pulse 1.2s infinite; }
.qa-badge.done { background: #67c23a; opacity: .85; }
@keyframes qa-pulse { 0% { box-shadow: 0 0 0 0 rgba(230,162,60,.6) } 70% { box-shadow: 0 0 0 10px rgba(230,162,60,0) } 100% { box-shadow: 0 0 0 0 rgba(230,162,60,0) } }
.qa-spot { position: fixed; border: 3px solid #e6a23c; border-radius: 6px; box-shadow: 0 0 0 9999px rgba(0,0,0,.10); pointer-events: none; z-index: 3001; transition: all .15s; }
.qa-tip { position: fixed; background: #fff; border: 1px solid #dcdfe6; border-radius: 8px; padding: 10px 12px; box-shadow: 0 6px 24px rgba(0,0,0,.2); pointer-events: auto; z-index: 3003; font-size: 13px; line-height: 1.5; max-width: 360px; }
.qa-tip.floating { width: 340px; }
.qa-tip-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.qa-tip-no { width: 22px; height: 22px; border-radius: 50%; background: #e6a23c; color: #fff; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }
.qa-tip-title { font-weight: 600; flex: 1; }
.qa-tip-body { margin-bottom: 8px; }
.qa-val { display: inline-block; margin-left: 6px; padding: 1px 8px; border: 1px solid #b3d8ff; background: #ecf5ff; border-radius: 4px; cursor: pointer; }
.qa-notfound { margin-top: 6px; color: #b26a00; background: #fdf6ec; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.qa-tip-actions { display: flex; gap: 4px; align-items: center; flex-wrap: wrap; }
.qa-bar { position: fixed; left: 0; right: 0; bottom: 0; height: 44px; background: #1f3a5f; color: #fff; display: flex; align-items: center; gap: 14px; padding: 0 16px; pointer-events: auto; z-index: 3003; font-size: 13px; box-shadow: 0 -2px 8px rgba(0,0,0,.2); }
.qa-bar .el-button.is-link { color: #ffd04b; }
.qa-bar .el-button.is-link.el-button--danger { color: #ff9b9b; }
.qa-pick-hint { position: fixed; top: 70px; left: 50%; transform: translateX(-50%); background: #e6a23c; color: #fff; padding: 6px 14px; border-radius: 20px; pointer-events: none; z-index: 3004; font-weight: 600; }
body.qa-picking * { cursor: crosshair !important; }
.qa-tour-layer .muted { color: #c0c4cc; font-size: 12px; }
.qa-tip .muted { color: #909399; }
</style>
